import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import goalService from './services/goalService';
import walletService from './services/walletService';
import { calculationService } from './services/calculationService';

const MyDashboard = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  
  // Goals state with hybrid loading
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for wallet tracking
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [walletsError, setWalletsError] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [bitcoinPrice, setBitcoinPrice] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  
  // State for saved calculations
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [calculationsLoading, setCalculationsLoading] = useState(true);
  const [calculationsError, setCalculationsError] = useState('');
  
  // State for modal and editing
  const [showNamingModal, setShowNamingModal] = useState(false);
  const [pendingAddress, setPendingAddress] = useState('');
  const [walletName, setWalletName] = useState('');
  const [editingWalletId, setEditingWalletId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingWallet, setSavingWallet] = useState(false);
  const [updatingWallet, setUpdatingWallet] = useState(false);
  const [deletingWallet, setDeletingWallet] = useState(false);

  // Load goals, wallets, and calculations with hybrid approach based on session
  useEffect(() => {
    fetchGoals();
    loadWallets();
    fetchBitcoinPrice();
    fetchCalculations();
  }, [session]);

  // Fetch saved calculations
  const fetchCalculations = async () => {
    if (!session) {
      // No session means no saved calculations
      setSavedCalculations([]);
      setCalculationsLoading(false);
      return;
    }

    setCalculationsLoading(true);
    setCalculationsError('');
    
    try {
      const { data, error } = await calculationService.getCalculations();
      
      if (error) {
        setCalculationsError(`Failed to load calculations: ${error.message}`);
        setSavedCalculations([]);
      } else {
        setSavedCalculations(data || []);
      }
    } catch (error) {
      console.error('Error fetching calculations:', error);
      setCalculationsError('An unexpected error occurred while loading calculations');
      setSavedCalculations([]);
    } finally {
      setCalculationsLoading(false);
    }
  };

  // Handle viewing/loading a calculation
  const handleViewCalculation = (calculation) => {
    // Route based on calculation type
    switch(calculation.calculation_type) {
      case 'Retirement':
        navigate('/retirement', { state: { savedCalculation: calculation } });
        break;
      case 'OpportunityCost':
        navigate('/opportunity-cost', { state: { savedCalculation: calculation } });
        break;
      case 'Interest':
        navigate('/interest', { state: { savedCalculation: calculation } });
        break;
      case 'LTV':
        navigate('/ltv', { state: { savedCalculation: calculation } });
        break;
      default:
        console.error(`Unknown calculation type: ${calculation.calculation_type}`);
        alert(`Unable to open calculation type: ${calculation.calculation_type}`);
        break;
    }
  };

  // Handle deleting a calculation
  const handleDeleteCalculation = async (calculationId) => {
    if (window.confirm('Are you sure you want to delete this saved calculation?')) {
      try {
        const { error } = await calculationService.deleteCalculation(calculationId);
        
        if (error) {
          setCalculationsError(`Failed to delete calculation: ${error.message}`);
        } else {
          // Remove from local state
          setSavedCalculations(prev => prev.filter(calc => calc.id !== calculationId));
        }
      } catch (error) {
        console.error('Error deleting calculation:', error);
        setCalculationsError('An unexpected error occurred while deleting the calculation');
      }
    }
  };

  // Hybrid goal fetching function
  const fetchGoals = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (session) {
        // User is logged in - fetch from Supabase
        const { data, error } = await goalService.getGoals();
        
        if (error) {
          setError(`Failed to load goals: ${error.message}`);
          setGoals([]);
        } else {
          setGoals(data || []);
        }
      } else {
        // User is a guest - load from localStorage
        const savedGoals = localStorage.getItem('bitcoinGoals');
        const parsedGoals = savedGoals ? JSON.parse(savedGoals) : [];
        setGoals(parsedGoals);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
      setError('An unexpected error occurred while loading goals');
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to map Supabase wallet to local format
  const mapSupabaseWalletToLocal = (supabaseWallet) => {
    return {
      id: supabaseWallet.id,
      address: supabaseWallet.wallet_address,
      name: supabaseWallet.wallet_name,
      balance: supabaseWallet.balance || null,
      lastUpdated: supabaseWallet.last_checked || supabaseWallet.updated_at,
      status: 'loading'
    };
  };

  // Hybrid wallet loading function
  const loadWallets = async () => {
    setWalletsLoading(true);
    setWalletsError('');
    
    try {
      if (session) {
        // User is logged in - fetch from Supabase
        const { data, error } = await walletService.getWallets();
        
        if (error) {
          setWalletsError(`Failed to load wallets: ${error.message}`);
          setWallets([]);
        } else {
          // Map Supabase wallets to local format
          const mappedWallets = (data || []).map(mapSupabaseWalletToLocal);
          setWallets(mappedWallets);
          
          // Fetch initial balances for all loaded wallets
          for (const wallet of mappedWallets) {
            await fetchBalance(wallet.address, wallet.id);
          }
        }
      } else {
        // User is a guest - load from localStorage
        const savedData = localStorage.getItem('trackedBitcoinAddresses');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          
          // Check if it's the old format (array of strings) or new format (array of objects)
          let walletsData;
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            if (typeof parsedData[0] === 'string') {
              // Old format - convert to new format
              walletsData = parsedData.map((addr, index) => ({
                id: Date.now().toString() + index,
                address: addr,
                name: `Wallet ${index + 1}`,
                balance: null,
                lastUpdated: null,
                status: 'loading'
              }));
              // Save the converted data
              localStorage.setItem('trackedBitcoinAddresses', JSON.stringify(walletsData));
            } else {
              // New format - use as is
              walletsData = parsedData;
            }
          } else {
            walletsData = [];
          }
          
          setWallets(walletsData);
          
          // Fetch initial balances for all loaded wallets
          for (const wallet of walletsData) {
            await fetchBalance(wallet.address);
          }
        }
      }
    } catch (error) {
      console.error('Error loading wallets:', error);
      setWalletsError('An unexpected error occurred while loading wallets');
    } finally {
      setWalletsLoading(false);
    }
  };

  // Fetch current Bitcoin price from CoinGecko
  const fetchBitcoinPrice = async () => {
    setIsLoadingPrice(true);
    try {
      const response = await axios.get(
        'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
      );
      const price = response.data.bitcoin.usd;
      setBitcoinPrice(price);
    } catch (error) {
      console.error('Error fetching Bitcoin price:', error);
      setBitcoinPrice(50000); // Fallback price
    } finally {
      setIsLoadingPrice(false);
    }
  };

  // Fetch wallet balance from Blockstream API and update Supabase if authenticated
  const fetchBalance = async (address, walletId = null) => {
    // Set wallet status to loading
    setWallets(prev => prev.map(wallet => 
      wallet.address === address
        ? { ...wallet, status: 'loading' }
        : wallet
    ));
    
    try {
      const response = await axios.get(
        `https://blockstream.info/api/address/${address}`
      );
      
      // Calculate balance in BTC from satoshis
      const balanceInSatoshis = 
        (response.data.chain_stats.funded_txo_sum || 0) - 
        (response.data.chain_stats.spent_txo_sum || 0);
      const balanceInBTC = balanceInSatoshis / 100000000;
      const lastChecked = new Date().toISOString();
      
      // Update wallet with balance and loaded status
      setWallets(prev => prev.map(wallet => 
        wallet.address === address
          ? {
              ...wallet,
              balance: balanceInBTC,
              lastUpdated: lastChecked,
              status: 'loaded'
            }
          : wallet
      ));
      
      // If authenticated and walletId provided, update balance in Supabase
      if (session && walletId) {
        try {
          await walletService.updateWallet(walletId, {
            balance: balanceInBTC,
            last_checked: lastChecked
          });
        } catch (updateError) {
          console.error('Error updating wallet balance in Supabase:', updateError);
          // Continue anyway - balance is shown locally
        }
      }
      
    } catch (error) {
      console.error(`Error fetching balance for ${address}:`, error);
      
      // Update wallet with error status
      setWallets(prev => prev.map(wallet => 
        wallet.address === address
          ? {
              ...wallet,
              status: 'error',
              errorMessage: 'Invalid Bitcoin address or API error.',
              lastUpdated: new Date().toISOString()
            }
          : wallet
      ));
    }
  };

  // Handle adding a new address - opens modal
  const handleAddAddress = () => {
    // Basic validation
    if (!newAddress.trim()) {
      alert('Please enter a Bitcoin address');
      return;
    }
    
    // Check if address already exists
    if (wallets.some(w => w.address === newAddress)) {
      alert('This address is already being tracked');
      return;
    }
    
    // Open naming modal
    setPendingAddress(newAddress);
    setWalletName('');
    setShowNamingModal(true);
  };

  // Handle saving wallet with name from modal - hybrid approach
  const handleSaveWallet = async () => {
    if (!walletName.trim()) {
      alert('Please enter a name for this wallet');
      return;
    }
    
    setSavingWallet(true);
    setWalletsError('');
    
    try {
      if (session) {
        // User is logged in - save to Supabase
        const { data, error } = await walletService.createWallet({
          address: pendingAddress,
          wallet_name: walletName.trim()
        });
        
        if (error) {
          setWalletsError(`Failed to save wallet: ${error.message}`);
          alert(`Failed to save wallet: ${error.message}`);
          setSavingWallet(false);
          return;
        }
        
        // Map the created wallet to local format and add to state
        const mappedWallet = mapSupabaseWalletToLocal(data);
        setWallets(prev => [...prev, mappedWallet]);
        
        // Fetch balance for the new wallet
        fetchBalance(mappedWallet.address, mappedWallet.id);
        
      } else {
        // User is a guest - save to localStorage
        const newWallet = {
          id: Date.now().toString(),
          address: pendingAddress,
          name: walletName.trim(),
          balance: null,
          lastUpdated: null,
          status: 'loading'
        };
        
        // Add to state
        const updatedWallets = [...wallets, newWallet];
        setWallets(updatedWallets);
        
        // Save to localStorage
        localStorage.setItem('trackedBitcoinAddresses', JSON.stringify(updatedWallets));
        
        // Fetch balance for the new wallet
        fetchBalance(newWallet.address);
      }
      
      // Close modal and clear inputs
      setShowNamingModal(false);
      setPendingAddress('');
      setWalletName('');
      setNewAddress('');
      
    } catch (error) {
      console.error('Unexpected error saving wallet:', error);
      setWalletsError('An unexpected error occurred. Please try again.');
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setSavingWallet(false);
    }
  };

  // Handle updating wallet name (inline editing) - hybrid approach
  const handleUpdateWalletName = async (walletId, newName) => {
    if (!newName.trim()) {
      // If empty, revert to original name
      setEditingWalletId(null);
      setEditingName('');
      return;
    }
    
    setUpdatingWallet(true);
    setWalletsError('');
    
    try {
      if (session) {
        // User is logged in - update in Supabase
        const { data, error } = await walletService.updateWallet(walletId, {
          wallet_name: newName.trim()
        });
        
        if (error) {
          setWalletsError(`Failed to update wallet: ${error.message}`);
          alert(`Failed to update wallet: ${error.message}`);
          setUpdatingWallet(false);
          return;
        }
        
        // Update local state with new name
        setWallets(prev => prev.map(wallet =>
          wallet.id === walletId
            ? { ...wallet, name: newName.trim() }
            : wallet
        ));
        
      } else {
        // User is a guest - update in localStorage
        const updatedWallets = wallets.map(wallet =>
          wallet.id === walletId
            ? { ...wallet, name: newName.trim() }
            : wallet
        );
        
        setWallets(updatedWallets);
        
        // Save to localStorage
        localStorage.setItem('trackedBitcoinAddresses', JSON.stringify(updatedWallets));
      }
      
      // Clear editing state
      setEditingWalletId(null);
      setEditingName('');
      
    } catch (error) {
      console.error('Unexpected error updating wallet:', error);
      setWalletsError('An unexpected error occurred. Please try again.');
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setUpdatingWallet(false);
    }
  };

  // Handle deleting a wallet - hybrid approach
  const handleDeleteWallet = async (walletId, walletName) => {
    if (window.confirm(`Are you sure you want to stop tracking "${walletName}"?`)) {
      setDeletingWallet(true);
      setWalletsError('');
      
      try {
        if (session) {
          // User is logged in - delete from Supabase
          const { error } = await walletService.deleteWallet(walletId);
          
          if (error) {
            setWalletsError(`Failed to delete wallet: ${error.message}`);
            alert(`Failed to delete wallet: ${error.message}`);
            setDeletingWallet(false);
            return;
          }
          
          // Remove from local state
          setWallets(prev => prev.filter(w => w.id !== walletId));
          
        } else {
          // User is a guest - delete from localStorage
          const updatedWallets = wallets.filter(w => w.id !== walletId);
          setWallets(updatedWallets);
          
          // Update localStorage
          localStorage.setItem('trackedBitcoinAddresses', JSON.stringify(updatedWallets));
        }
        
      } catch (error) {
        console.error('Unexpected error deleting wallet:', error);
        setWalletsError('An unexpected error occurred. Please try again.');
        alert('An unexpected error occurred. Please try again.');
      } finally {
        setDeletingWallet(false);
      }
    }
  };

  // Handle refreshing a wallet balance
  const handleRefreshWallet = (address, walletId = null) => {
    // Find wallet ID if not provided (for authenticated users)
    if (session && !walletId) {
      const wallet = wallets.find(w => w.address === address);
      walletId = wallet?.id;
    }
    fetchBalance(address, walletId);
  };

  // Helper functions for hybrid data access (Supabase vs localStorage)
  const getGoalName = (goal) => goal.goal_name ?? goal.goalName ?? 'Unnamed Goal';
  const getTargetAmount = (goal) => goal.target_amount ?? goal.targetAmount ?? 0;
  const getMonthlyContribution = (goal) => goal.goal_details?.monthly_contribution ?? goal.monthlyContribution ?? 0;
  const getStrategy = (goal) => goal.goal_details?.strategy ?? goal.strategy ?? 'goal';
  const getMonthsCompleted = (goal) => goal.goal_details?.months_completed ?? goal.monthsCompleted ?? 0;
  const getTotalMonths = (goal) => goal.goal_details?.total_months ?? goal.totalMonths ?? 1;
  const getCurrentProgress = (goal) => goal.goal_details?.current_progress ?? goal.currentProgress ?? 0;
  const getCreatedDate = (goal) => goal.created_at ?? goal.createdDate ?? new Date().toISOString();

  // Mine next block - increment monthsCompleted for specific goal
  const mineNextBlock = async (goalId) => {
    if (session) {
      // Authenticated user - implement hybrid update logic
      const goalToUpdate = goals.find(goal => goal.id === goalId);
      if (goalToUpdate) {
        const currentMonthsCompleted = getMonthsCompleted(goalToUpdate);
        const totalMonths = getTotalMonths(goalToUpdate);
        
        if (currentMonthsCompleted < totalMonths) {
          const newMonthsCompleted = currentMonthsCompleted + 1;
          const newProgress = (newMonthsCompleted / totalMonths) * 100;
          
          // Optimistic UI Update: Immediately update local state for instant feedback
          const updatedGoals = goals.map(goal => {
            if (goal.id === goalId) {
              // Update based on data structure
              if (goal.goal_details) {
                // Supabase structure
                return {
                  ...goal,
                  goal_details: {
                    ...goal.goal_details,
                    months_completed: newMonthsCompleted,
                    current_progress: newProgress
                  }
                };
              } else {
                // localStorage structure
                return {
                  ...goal,
                  monthsCompleted: newMonthsCompleted,
                  currentProgress: newProgress
                };
              }
            }
            return goal;
          });
          setGoals(updatedGoals);

          // Background persistence: Save to Supabase
          try {
            const updates = {
              goal_details: {
                ...goalToUpdate.goal_details,
                months_completed: newMonthsCompleted,
                current_progress: newProgress
              }
            };

            const { data, error } = await goalService.updateGoal(goalId, updates);
            
            if (error) {
              console.error('Failed to save goal progress to database:', error.message);
              // Optionally show user notification about sync failure
              // Could also implement state reversion here if desired
            } else {
              console.log('Goal progress successfully saved to database');
            }
          } catch (error) {
            console.error('Unexpected error saving goal progress:', error);
            // Optionally show user notification about sync failure
          }
        }
      }
    } else {
      // Guest user - update localStorage
      const updatedGoals = goals.map(goal => {
        if (goal.id === goalId) {
          const currentMonthsCompleted = getMonthsCompleted(goal);
          const totalMonths = getTotalMonths(goal);
          
          if (currentMonthsCompleted < totalMonths) {
            const newMonthsCompleted = currentMonthsCompleted + 1;
            const newProgress = (newMonthsCompleted / totalMonths) * 100;
            
            return {
              ...goal,
              monthsCompleted: newMonthsCompleted,
              currentProgress: newProgress
            };
          }
        }
        return goal;
      });

      // Update state and save to localStorage
      setGoals(updatedGoals);
      localStorage.setItem('bitcoinGoals', JSON.stringify(updatedGoals));
    }
  };

  // Generate blockchain visualization blocks
  const generateBlockchain = (goal) => {
    const totalMonths = getTotalMonths(goal);
    const monthsCompleted = getMonthsCompleted(goal);
    const totalBlocks = Math.min(totalMonths, 60); // Cap at 60 blocks for visual purposes
    const blocksPerRow = 12; // 12 blocks per row
    const minedBlocks = Math.floor((monthsCompleted / totalMonths) * totalBlocks);
    
    const blocks = [];
    for (let i = 0; i < totalBlocks; i++) {
      const isMined = i < minedBlocks;
      blocks.push(
        <div
          key={i}
          className={`blockchain-block ${isMined ? 'mined' : 'unmined'}`}
          title={isMined ? `Block ${i + 1} - Mined ✅` : `Block ${i + 1} - Pending ⏳`}
        >
          {isMined ? '₿' : '⬜'}
        </div>
      );
    }

    // Group blocks into rows
    const rows = [];
    for (let i = 0; i < blocks.length; i += blocksPerRow) {
      rows.push(
        <div key={i / blocksPerRow} className="blockchain-row">
          {blocks.slice(i, i + blocksPerRow)}
        </div>
      );
    }

    return rows;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Hybrid goal deletion
  const deleteGoal = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      if (session) {
        // Authenticated user - delete from Supabase
        try {
          const { error } = await goalService.deleteGoal(goalId);
          if (error) {
            setError(`Failed to delete goal: ${error.message}`);
            return;
          }
          // Remove from local state
          setGoals(goals.filter(goal => goal.id !== goalId));
        } catch (error) {
          console.error('Error deleting goal:', error);
          setError('Failed to delete goal. Please try again.');
        }
      } else {
        // Guest user - delete from localStorage
        const updatedGoals = goals.filter(goal => goal.id !== goalId);
        setGoals(updatedGoals);
        localStorage.setItem('bitcoinGoals', JSON.stringify(updatedGoals));
      }
    }
  };

  if (loading) {
    return (
      <div className="calculator-container">
        <div style={{ textAlign: 'center', padding: '4rem', color: '#EAEAEA' }}>
          Loading your dashboard...
        </div>
      </div>
    );
  }

  // Check if everything is truly empty for the global empty state
  const isCompletelyEmpty = goals.length === 0 && wallets.length === 0 && savedCalculations.length === 0 && !session;

  return (
    <div className="calculator-container">
      <h2 className="calculator-title">⛏️ My Dashboard</h2>
      
      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: 'rgba(217, 83, 79, 0.1)',
          border: '1px solid rgba(217, 83, 79, 0.3)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          color: '#d9534f',
          textAlign: 'center'
        }}>
          <strong>⚠️ Error:</strong> {error}
          <button 
            onClick={fetchGoals}
            style={{
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#dc3545',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            🔄 Retry
          </button>
        </div>
      )}
      
      {/* Naming Modal */}
      {showNamingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1E1E1E',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #FF8C00',
            boxShadow: '0 10px 30px rgba(255, 140, 0, 0.3)'
          }}>
            <h3 style={{ color: '#FF8C00', marginBottom: '1.5rem' }}>
              Name Your Wallet
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#AAAAAA', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                Bitcoin Address:
              </label>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#2A2A2A',
                borderRadius: '8px',
                color: '#EAEAEA',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                wordBreak: 'break-all'
              }}>
                {pendingAddress}
              </div>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#AAAAAA', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                Wallet Name:
              </label>
              <input
                type="text"
                value={walletName}
                onChange={(e) => setWalletName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !savingWallet && handleSaveWallet()}
                placeholder="e.g., Cold Storage, Savings, Trading"
                autoFocus
                disabled={savingWallet}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: '#2A2A2A',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#EAEAEA',
                  fontSize: '1rem',
                  outline: 'none',
                  opacity: savingWallet ? 0.6 : 1
                }}
              />
            </div>
            
            {!session && (
              <div style={{
                backgroundColor: 'rgba(255, 165, 0, 0.1)',
                border: '1px solid rgba(255, 165, 0, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#FFA500',
                fontSize: '0.9rem'
              }}>
                💡 Sign up to save wallets permanently and sync across devices!
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowNamingModal(false);
                  setPendingAddress('');
                  setWalletName('');
                }}
                disabled={savingWallet}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#2A2A2A',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#EAEAEA',
                  cursor: savingWallet ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  opacity: savingWallet ? 0.6 : 1
                }}
                onMouseEnter={(e) => !savingWallet && (e.target.style.backgroundColor = '#333')}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2A2A2A'}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveWallet}
                disabled={savingWallet}
                className="calculate-button"
                style={{
                  background: savingWallet 
                    ? 'linear-gradient(135deg, #666, #888)' 
                    : 'linear-gradient(135deg, #FF8C00, #FFA500)',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  cursor: savingWallet ? 'not-allowed' : 'pointer',
                  opacity: savingWallet ? 0.6 : 1
                }}
              >
                {savingWallet ? 'Saving...' : 'Save Wallet'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Show global empty state only if truly everything is empty */}
      {isCompletelyEmpty ? (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4rem 1rem',
            textAlign: 'center'
        }}>
            <div style={{
                maxWidth: '500px',
                backgroundColor: '#2A2A2A',
                border: '1px solid #333',
                borderRadius: '12px',
                padding: '2.5rem'
            }}>
                <div style={{
                    margin: '0 auto 1.5rem auto',
                    width: '4rem',
                    height: '4rem',
                    borderRadius: '9999px',
                    backgroundColor: '#3A3A3A',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-400">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    </svg>
                </div>
                <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: '#FFFFFF',
                    marginBottom: '0.75rem'
                }}>
                    Your Personal Dashboard Awaits
                </h3>
                <p style={{
                    color: '#BDBDBD',
                    marginBottom: '1rem',
                    lineHeight: '1.6'
                }}>
                    You have no saved goals yet. Visit the Goal Planner to create your first plan! Sign up to save goals permanently and access them on any device.
                </p>
                <p style={{
                  color: '#FFA500',
                  marginBottom: '1.5rem',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  💡 Sign up to save permanently and access on any device!
                </p>
                <button
                    onClick={() => navigate('/goal-planner')}
                    style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #FF8C00, #FFA500)',
                        color: 'white',
                        fontWeight: '600',
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'linear-gradient(135deg, #FFA500, #FFB52E)'}
                    onMouseLeave={(e) => e.target.style.background = 'linear-gradient(135deg, #FF8C00, #FFA500)'}
                >
                    Create Your First Goal
                </button>
            </div>
        </div>
      ) : (
        <div className="dashboard-content">
          <p className="dashboard-intro">
            Track your progress toward financial freedom with your personal Bitcoin mining dashboard. 
            Each month's contribution mines a new block in your financial blockchain!
            {session && (
              <span style={{ color: '#28a745', fontWeight: '500', marginLeft: '0.5rem' }}>
                ✅ Synced with your account
              </span>
            )}
          </p>

          {/* Goals Section - Independent */}
          {goals.length > 0 && (
            <>
              {goals.map((goal, index) => (
                <div key={goal.id} className="mining-dashboard">
                  {/* Dashboard Header */}
                  <div className="dashboard-header">
                    <h3 className="goal-title">
                      {getGoalName(goal)}
                      <span className="goal-strategy">
                        ({getStrategy(goal) === 'sell' ? '🔽 Sell Strategy' : '💰 Borrow Strategy'})
                      </span>
                    </h3>
                    <button 
                      className="delete-goal-btn"
                      onClick={() => deleteGoal(goal.id)}
                      title="Delete Goal"
                    >
                      🗑️
                    </button>
                  </div>

                  {/* Mining Metrics */}
                  <div className="mining-metrics">
                    <div className="metric-card hash-rate">
                      <div className="metric-icon">⛏️</div>
                      <div className="metric-content">
                        <div className="metric-label">YOUR HASH RATE</div>
                        <div className="metric-value">{formatCurrency(getMonthlyContribution(goal))}/month</div>
                      </div>
                    </div>

                    <div className="metric-card difficulty">
                      <div className="metric-icon">🎯</div>
                      <div className="metric-content">
                        <div className="metric-label">GOAL DIFFICULTY</div>
                        <div className="metric-value">{formatCurrency(getTargetAmount(goal))}</div>
                      </div>
                    </div>

                    <div className="metric-card blocks-mined">
                      <div className="metric-icon">⛏️</div>
                      <div className="metric-content">
                        <div className="metric-label">BLOCKS MINED</div>
                        <div className="metric-value">
                          {getMonthsCompleted(goal)}/{getTotalMonths(goal)}
                          <div className="progress-percentage">
                            ({Math.round(getCurrentProgress(goal))}% complete)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="progress-bar-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${getCurrentProgress(goal)}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">
                      {getMonthsCompleted(goal)} months of {getTotalMonths(goal)} months completed
                    </div>
                  </div>

                  {/* Blockchain Visualization */}
                  <div className="blockchain-section">
                    <h4 className="blockchain-title">Personal Blockchain</h4>
                    <div className="blockchain-container">
                      {generateBlockchain(goal)}
                    </div>
                    <div className="blockchain-legend">
                      <div className="legend-item">
                        <span className="legend-icon mined">₿</span>
                        <span>Mined Block (Month Completed)</span>
                      </div>
                      <div className="legend-item">
                        <span className="legend-icon unmined">⬜</span>
                        <span>Unmined Block (Pending Month)</span>
                      </div>
                    </div>
                  </div>

                  {/* Mining Action */}
                  <div className="mining-action">
                    <button
                      className="mine-button"
                      onClick={() => mineNextBlock(goal.id)}
                      disabled={getMonthsCompleted(goal) >= getTotalMonths(goal)}
                    >
                      {getMonthsCompleted(goal) >= getTotalMonths(goal) 
                        ? '🎉 Goal Completed!' 
                        : '⛏️ Confirm This Month\'s Contribution & Mine Next Block'
                      }
                    </button>
                    
                    {getMonthsCompleted(goal) >= getTotalMonths(goal) && (
                      <div className="completion-message">
                        <h4>🎯 Congratulations!</h4>
                        <p>You've successfully completed your "{getGoalName(goal)}" goal! 
                           Your consistency and dedication have built a solid foundation for your financial future.</p>
                      </div>
                    )}
                  </div>

                  {/* Goal Details */}
                  <div className="goal-details">
                    <div className="detail-item">
                      <span className="detail-label">Created:</span>
                      <span className="detail-value">
                        {new Date(getCreatedDate(goal)).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Time Frame:</span>
                      <span className="detail-value">
                        {Math.round(getTotalMonths(goal) / 12)} years ({getTotalMonths(goal)} months)
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Total Contributions:</span>
                      <span className="detail-value">
                        {formatCurrency(getMonthsCompleted(goal) * getMonthlyContribution(goal))} of {formatCurrency(getTotalMonths(goal) * getMonthlyContribution(goal))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add New Goal Button */}
              <div className="add-goal-section">
                <button 
                  className="calculate-button"
                  onClick={() => navigate('/goal-planner')}
                  style={{ 
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    marginTop: '2rem'
                  }}
                >
                  ➕ Create New Goal
                </button>
              </div>
            </>
          )}

          {/* On-Chain Balance Tracker - Independent */}
          <div className="mining-dashboard" style={{ marginTop: '3rem' }}>
            <div className="dashboard-header">
              <h3 className="goal-title">
                ⛓️ On-Chain Balance Tracker
              </h3>
            </div>

            <p style={{ 
              color: '#BDBDBD', 
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Track your Bitcoin wallet balances in real-time. Add any public Bitcoin address to monitor its balance 
              and total value. 
              {session 
                ? ' Your wallets are securely saved to your account and synced across all devices.'
                : ' All data is stored locally in your browser for maximum privacy.'}
            </p>

            {/* Wallets Error Message */}
            {walletsError && (
              <div style={{
                backgroundColor: 'rgba(217, 83, 79, 0.1)',
                border: '1px solid rgba(217, 83, 79, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#d9534f',
                textAlign: 'center'
              }}>
                <strong>⚠️ Wallet Error:</strong> {walletsError}
              </div>
            )}

            {/* Add Address Form */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              <input
                type="text"
                placeholder="Enter Bitcoin address (e.g., bc1q...)"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddAddress()}
                style={{
                  flex: '1',
                  minWidth: '300px',
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  backgroundColor: '#2A2A2A',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#EAEAEA',
                  outline: 'none'
                }}
              />
              <button
                onClick={handleAddAddress}
                className="calculate-button"
                style={{
                  background: 'linear-gradient(135deg, #FF8C00, #FFA500)',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem'
                }}
              >
                ➕ Add Address
              </button>
            </div>

            {/* Wallet List */}
            {walletsLoading ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#EAEAEA'
              }}>
                Loading wallets...
              </div>
            ) : wallets.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                backgroundColor: '#2A2A2A',
                borderRadius: '8px',
                color: '#AAAAAA'
              }}>
                <p>No wallets tracked yet. Add a Bitcoin address above to get started!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {wallets.map((wallet) => {
                  const usdValue = wallet.balance && bitcoinPrice 
                    ? (wallet.balance * bitcoinPrice).toFixed(2)
                    : null;
                  const isEditing = editingWalletId === wallet.id;
                  
                  // Render based on wallet status
                  if (wallet.status === 'loading') {
                    // Skeleton Loading Card
                    return (
                      <div 
                        key={wallet.id}
                        className="skeleton-card"
                        style={{
                          gap: '1rem'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: '1rem'
                        }}>
                          <div style={{ flex: '1', minWidth: '200px' }}>
                            <div className="skeleton skeleton-title"></div>
                            <div className="skeleton skeleton-text" style={{ width: '50%' }}></div>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            gap: '2rem',
                            alignItems: 'center'
                          }}>
                            <div>
                              <div className="skeleton skeleton-text" style={{ width: '60px', height: '0.8rem' }}></div>
                              <div className="skeleton skeleton-balance"></div>
                            </div>
                            <div>
                              <div className="skeleton skeleton-text" style={{ width: '80px', height: '0.8rem' }}></div>
                              <div className="skeleton skeleton-balance"></div>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderTop: '1px solid #444',
                          paddingTop: '1rem'
                        }}>
                          <div className="skeleton skeleton-text" style={{ width: '150px' }}></div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <div className="skeleton" style={{ width: '80px', height: '2rem' }}></div>
                            <div className="skeleton" style={{ width: '70px', height: '2rem' }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  if (wallet.status === 'error') {
                    // Error Card
                    return (
                      <div 
                        key={wallet.id}
                        className="metric-card"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1rem',
                          padding: '1.5rem',
                          borderColor: '#dc3545'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: '1rem'
                        }}>
                          <div style={{ flex: '1', minWidth: '200px' }}>
                            {/* Wallet Name - Clickable for editing */}
                            {isEditing ? (
                              <input
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleUpdateWalletName(wallet.id, editingName);
                                  } else if (e.key === 'Escape') {
                                    setEditingWalletId(null);
                                    setEditingName('');
                                  }
                                }}
                                onBlur={() => handleUpdateWalletName(wallet.id, editingName)}
                                autoFocus
                                disabled={updatingWallet}
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  color: '#EAEAEA',
                                  backgroundColor: '#2A2A2A',
                                  border: '1px solid #FF8C00',
                                  borderRadius: '4px',
                                  padding: '0.25rem 0.5rem',
                                  width: '100%',
                                  outline: 'none',
                                  opacity: updatingWallet ? 0.6 : 1
                                }}
                              />
                            ) : (
                              <div 
                                onClick={() => {
                                  setEditingWalletId(wallet.id);
                                  setEditingName(wallet.name);
                                }}
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  color: '#EAEAEA',
                                  cursor: 'pointer',
                                  marginBottom: '0.5rem',
                                  transition: 'color 0.3s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#FF8C00'}
                                onMouseLeave={(e) => e.target.style.color = '#EAEAEA'}
                                title="Click to edit name"
                              >
                                {wallet.name}
                              </div>
                            )}
                            
                            {/* Bitcoin Address */}
                            <div style={{ 
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: '#AAAAAA',
                              wordBreak: 'break-all'
                            }}>
                              {wallet.address.slice(0, 12)}...{wallet.address.slice(-12)}
                            </div>
                          </div>

                          <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <div style={{ 
                              color: '#dc3545',
                              fontSize: '0.9rem',
                              textAlign: 'center'
                            }}>
                              ⚠️ {wallet.errorMessage}
                            </div>
                            <button
                              onClick={() => handleRefreshWallet(wallet.address, wallet.id)}
                              className="calculate-button"
                              style={{
                                background: 'linear-gradient(135deg, #dc3545, #c82333)',
                                padding: '0.5rem 1rem',
                                fontSize: '0.85rem'
                              }}
                            >
                              🔄 Retry
                            </button>
                          </div>
                        </div>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderTop: '1px solid #444',
                          paddingTop: '1rem'
                        }}>
                          <div style={{ 
                            fontSize: '0.75rem',
                            color: '#AAAAAA'
                          }}>
                            {wallet.lastUpdated && (
                              <>Last attempt: {new Date(wallet.lastUpdated).toLocaleString()}</>
                            )}
                          </div>
                          
                          <button
                            onClick={() => handleDeleteWallet(wallet.id, wallet.name)}
                            disabled={deletingWallet}
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.85rem',
                              backgroundColor: deletingWallet ? '#666' : '#dc3545',
                              border: 'none',
                              borderRadius: '6px',
                              color: 'white',
                              cursor: deletingWallet ? 'not-allowed' : 'pointer',
                              transition: 'all 0.3s ease',
                              opacity: deletingWallet ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !deletingWallet && (e.target.style.backgroundColor = '#c82333')}
                            onMouseLeave={(e) => !deletingWallet && (e.target.style.backgroundColor = '#dc3545')}
                          >
                            🗑️ {deletingWallet ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    );
                  }
                  
                  // Loaded State (existing successful wallet display)
                  return (
                    <div 
                      key={wallet.id}
                      className="metric-card"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        padding: '1.5rem'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: '1rem'
                      }}>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                          {/* Wallet Name - Clickable for editing */}
                          {isEditing ? (
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdateWalletName(wallet.id, editingName);
                                } else if (e.key === 'Escape') {
                                  setEditingWalletId(null);
                                  setEditingName('');
                                }
                              }}
                              onBlur={() => handleUpdateWalletName(wallet.id, editingName)}
                              autoFocus
                              disabled={updatingWallet}
                              style={{
                                fontSize: '1.2rem',
                                fontWeight: '600',
                                color: '#EAEAEA',
                                backgroundColor: '#2A2A2A',
                                border: '1px solid #FF8C00',
                                borderRadius: '4px',
                                padding: '0.25rem 0.5rem',
                                width: '100%',
                                outline: 'none',
                                opacity: updatingWallet ? 0.6 : 1
                              }}
                            />
                          ) : (
                            <div 
                              onClick={() => {
                                setEditingWalletId(wallet.id);
                                setEditingName(wallet.name);
                              }}
                              style={{
                                fontSize: '1.2rem',
                                fontWeight: '600',
                                color: '#EAEAEA',
                                cursor: 'pointer',
                                marginBottom: '0.5rem',
                                transition: 'color 0.3s ease'
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#FF8C00'}
                              onMouseLeave={(e) => e.target.style.color = '#EAEAEA'}
                              title="Click to edit name"
                            >
                              {wallet.name}
                            </div>
                          )}
                          
                          {/* Bitcoin Address */}
                          <div style={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: '#AAAAAA',
                            wordBreak: 'break-all'
                          }}>
                            {wallet.address.slice(0, 12)}...{wallet.address.slice(-12)}
                          </div>
                        </div>

                        {wallet.balance !== null && (
                          <div style={{ 
                            display: 'flex', 
                            gap: '2rem',
                            alignItems: 'center'
                          }}>
                            <div>
                              <div style={{ 
                                color: '#FF8C00', 
                                fontSize: '0.8rem',
                                marginBottom: '0.25rem'
                              }}>
                                BALANCE
                              </div>
                              <div style={{ 
                                fontSize: '1.2rem',
                                fontWeight: '600',
                                color: '#EAEAEA'
                              }}>
                                ₿ {wallet.balance.toFixed(8)}
                              </div>
                            </div>
                            
                            {usdValue && (
                              <div>
                                <div style={{ 
                                  color: '#FF8C00', 
                                  fontSize: '0.8rem',
                                  marginBottom: '0.25rem'
                                }}>
                                  USD VALUE
                                </div>
                                <div style={{ 
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  color: '#28a745'
                                }}>
                                  ${formatCurrency(usdValue).replace('$', '')}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: '1px solid #444',
                        paddingTop: '1rem'
                      }}>
                        <div style={{ 
                          fontSize: '0.75rem',
                          color: '#AAAAAA'
                        }}>
                          {wallet.lastUpdated && (
                            <>Last updated: {new Date(wallet.lastUpdated).toLocaleString()}</>
                          )}
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem' 
                        }}>
                          <button
                            onClick={() => handleRefreshWallet(wallet.address, wallet.id)}
                            disabled={wallet.status === 'loading'}
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.85rem',
                              backgroundColor: '#2A2A2A',
                              border: '1px solid #444',
                              borderRadius: '6px',
                              color: '#EAEAEA',
                              cursor: wallet.status === 'loading' ? 'not-allowed' : 'pointer',
                              opacity: wallet.status === 'loading' ? 0.5 : 1,
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => wallet.status !== 'loading' && (e.target.style.backgroundColor = '#333')}
                            onMouseLeave={(e) => wallet.status !== 'loading' && (e.target.style.backgroundColor = '#2A2A2A')}
                          >
                            🔄 Refresh
                          </button>
                          
                          <button
                            onClick={() => handleDeleteWallet(wallet.id, wallet.name)}
                            disabled={deletingWallet}
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.85rem',
                              backgroundColor: deletingWallet ? '#666' : '#dc3545',
                              border: 'none',
                              borderRadius: '6px',
                              color: 'white',
                              cursor: deletingWallet ? 'not-allowed' : 'pointer',
                              transition: 'all 0.3s ease',
                              opacity: deletingWallet ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !deletingWallet && (e.target.style.backgroundColor = '#c82333')}
                            onMouseLeave={(e) => !deletingWallet && (e.target.style.backgroundColor = '#dc3545')}
                          >
                            🗑️ {deletingWallet ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Saved Calculations Section - Independent for authenticated users */}
          {session && (
            <div className="mining-dashboard" style={{ marginTop: '3rem' }}>
              <div className="dashboard-header">
                <h3 className="goal-title">
                  📝 My Saved Calculations
                </h3>
              </div>

              <p style={{ 
                color: '#BDBDBD', 
                marginBottom: '2rem',
                lineHeight: '1.6'
              }}>
                View and manage your saved calculations from various financial planning tools. 
                These calculations are securely stored in your account and synced across all devices.
              </p>

              {/* Calculations Error Message */}
              {calculationsError && (
                <div style={{
                  backgroundColor: 'rgba(217, 83, 79, 0.1)',
                  border: '1px solid rgba(217, 83, 79, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  color: '#d9534f',
                  textAlign: 'center'
                }}>
                  <strong>⚠️ Calculations Error:</strong> {calculationsError}
                  <button 
                    onClick={fetchCalculations}
                    style={{
                      marginLeft: '1rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#dc3545',
                      border: 'none',
                      borderRadius: '4px',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Retry
                  </button>
                </div>
              )}

              {/* Calculations List */}
              {calculationsLoading ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#EAEAEA'
                }}>
                  Loading saved calculations...
                </div>
              ) : savedCalculations.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  backgroundColor: '#2A2A2A',
                  borderRadius: '8px',
                  color: '#AAAAAA'
                }}>
                  <p>You have no saved calculations yet.</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Use the calculation tools (like the Retirement Calculator) and save your results to see them here!
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {savedCalculations.map((calculation) => (
                    <div 
                      key={calculation.id}
                      className="metric-card"
                      onClick={() => handleViewCalculation(calculation)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1.5rem',
                        flexWrap: 'wrap',
                        gap: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#333'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#2A2A2A'}
                    >
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <div style={{
                          fontSize: '1.2rem',
                          fontWeight: '600',
                          color: '#EAEAEA',
                          marginBottom: '0.5rem'
                        }}>
                          {calculation.calculation_type === 'Retirement' ? '🏦 Retirement Plan' : 
                           calculation.calculation_type === 'Interest' ? '📈 Interest Calculation' :
                           calculation.calculation_type === 'LTV' ? '🏠 LTV Calculation' :
                           calculation.calculation_type === 'OpportunityCost' ? '💰 Opportunity Cost' :
                           `📊 ${calculation.calculation_type} Calculation`}
                        </div>
                        <div style={{ 
                          fontSize: '0.9rem',
                          color: '#AAAAAA'
                        }}>
                          Saved on: {new Date(calculation.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCalculation(calculation.id);
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.85rem',
                          backgroundColor: '#dc3545',
                          border: 'none',
                          borderRadius: '6px',
                          color: 'white',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyDashboard;